import {Store} from 'redux';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {FrontConfig} from '../../../srcCommon/bundler/bundlerDefineConfig';
import {isBadCommit} from '../../../srcCommon/helpers/appUpdateHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {AppState} from '../../store/appState';
import {criticalUpdateAvailableAction} from '../../store/update/updateActions';
import {reloadBrowser} from '../browser';
import {HttpResponse, spyOnHttpResponses} from '../http/httpBase';
import {
  collectServiceWorkerErrorData,
  isWaitingServiceWorker,
  reloadServiceWorker,
  unregisterServiceWorker,
} from '../serviceWorker/bridge/serviceWorkerBridge';

const reloadTimeStorageKey = 'criticalUpdateReloadTime';
const reloadDelayMs = 60 * 1000;
let broadcastChannel: BroadcastChannel | undefined;
let reloadTimeout = 0;

function criticalUpdateAvailable(store: Store<AppState>, reloadTime: number) {
  // Don't start a countdown unless there's a waiting worker, since we
  // may have come back after a manual reload such that the reload time
  // wasn't removed from localStorage.
  if (!isWaitingServiceWorker()) {
    return;
  }

  // If we've already started a countdown, ignore subsequent updates
  if (reloadTimeout) {
    return;
  }

  const currentReloadDelayMs = reloadTime - Date.now();

  // If the reload time is in the past or far in the future, ignore it.
  if (currentReloadDelayMs < 0 || currentReloadDelayMs > reloadDelayMs) {
    return;
  }

  reloadTimeout = globalWindow.setInstrumentedTimeout(applyCriticalUpdate, currentReloadDelayMs);
  store.dispatch(criticalUpdateAvailableAction(Number(reloadTime)));
}

function checkForStoredCriticalUpdate(store: Store<AppState>) {
  const criticalUpdateReloadTimeString = localStorage.getItem(reloadTimeStorageKey);
  if (!criticalUpdateReloadTimeString) {
    return;
  }

  criticalUpdateAvailable(store, Number(criticalUpdateReloadTimeString));
}

export function setupCriticalUpdate(store: Store<AppState>) {
  try {
    if ('BroadcastChannel' in globalWindow) {
      broadcastChannel = new BroadcastChannel(reloadTimeStorageKey);
      broadcastChannel.addEventListener('message', (event) => {
        criticalUpdateAvailable(store, event.data);
      });
    } else {
      checkForStoredCriticalUpdate(store);
      globalWindow.addEventListener('storage', () => checkForStoredCriticalUpdate(store));
    }
  } catch (error) {
    reportToBugsnag(error);
  }

  const stopSpying = spyOnHttpResponses((response: HttpResponse) => {
    const minBuildTimestamp = response.headers['x-front-min-build-timestamp'];
    const badCommits = response.headers['x-front-bad-commits'];
    if (!isCriticalUpdateAvailable(minBuildTimestamp, badCommits)) {
      return;
    }

    if (!isWaitingServiceWorker()) {
      return;
    }

    stopSpying();

    // If we've already started a countdown, don't start a new one.
    if (reloadTimeout) {
      return;
    }

    const reloadTime = Date.now() + reloadDelayMs;
    try {
      if (broadcastChannel) {
        setInterval(() => broadcastChannel && broadcastChannel.postMessage(reloadTime), 1000);
      } else {
        localStorage.setItem(reloadTimeStorageKey, String(reloadTime));
      }
    } catch (error) {
      reportToBugsnag(error);
    }

    // We need to call this explicitly since we won't receive our own
    // broadcast message and the 'storage' event only fires if modified
    // in the context of another document.
    criticalUpdateAvailable(store, reloadTime);
  });
}

export function isCriticalUpdateAvailable(
  minBuildTimestamp: string | undefined,
  badCommits: string | undefined,
) {
  if (minBuildTimestamp && new Date(FrontConfig.BUILD_TIMESTAMP) < new Date(minBuildTimestamp)) {
    return true;
  }

  if (isBadCommit(FrontConfig.GIT_COMMIT, badCommits)) {
    return true;
  }

  return false;
}

export async function applyCriticalUpdate() {
  if (!broadcastChannel) {
    localStorage.removeItem(reloadTimeStorageKey);
  }

  if (isWaitingServiceWorker()) {
    reloadServiceWorker();

    // If we haven't reloaded in 10 sec due to a service worker controller change,
    // the service worker might be stuck and skipWaiting isn't working.
    // Unregister the service worker to force it.
    setTimeout(async () => {
      await unregisterServiceWorker();
      await reportToBugsnag(
        new ErrorWithData(
          'applyCriticalUpdate waited too longer for skipWaiting',
          collectServiceWorkerErrorData(),
        ),
      );
      reloadBrowser();
    }, 10 * 1000);
    return;
  }

  // We shouldn't have gotten to this point without a waiting worker,
  // so report an error and reload the page just in case.
  await reportToBugsnag(
    new ErrorWithData('applyCriticalUpdate reached without waiting worker', collectServiceWorkerErrorData()),
  );
  reloadBrowser();
}
